/**
 * Zeichnen der Vektor-Notizen. Punkte sind auf die Seite normalisiert (0–1),
 * Strichstärke relativ zur Seitenbreite – unabhängig von Zoom und Gerät.
 */
import type { Stroke } from "@piano/shared";

export type Tool = "pen" | "highlighter" | "eraser";
export type Point = [number, number, number];

/** Bearbeitbarer Strich (vor der Normalisierung durch das Schema). */
export interface DraftStroke {
  id: string;
  tool: Tool;
  mode: "draw" | "erase";
  color: string;
  alpha: number;
  size: number;
  hasPressure: boolean;
  points: Point[];
}

export const INK_COLORS = [
  { name: "Schwarz", value: "#1F1F1F" },
  { name: "Blau", value: "#2563EB" },
  { name: "Rot", value: "#E5484D" },
  { name: "Grün", value: "#10B981" },
  { name: "Orange", value: "#F97316" },
  { name: "Gelb", value: "#FACC15" },
] as const;

/** Wählbare Strichstärken (Stufen 1–8 laut Preset-Schema) */
export const INK_SIZES = [1, 2, 3, 4, 6, 8] as const;

export const TOOL_WIDTH: Record<Tool, number> = { pen: 1, highlighter: 2.4, eraser: 0 };
export const HIGHLIGHT_ALPHA = 0.35;
const ERASER_RADIUS = 0.035;
export const MIN_POINT_DISTANCE = 0.0004;

function pressureScale(stroke: Pick<Stroke, "hasPressure">, pressure: number | undefined): number {
  if (!stroke.hasPressure) return 1;
  const p = Math.max(0.05, Math.min(1, pressure ?? 0.5));
  return 0.45 + p * 0.85;
}

/** Zeichnet einen Strich ab `from` (für inkrementelles Live-Zeichnen). */
export function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke | DraftStroke, from = 1): void {
  const { points } = stroke;
  if (!points.length) return;
  const w = ctx.canvas.width || 1;
  const h = ctx.canvas.height || 1;
  const erase = stroke.mode === "erase";
  const base = Math.max(0.5, stroke.size * w);
  ctx.save();
  ctx.globalCompositeOperation = erase ? "destination-out" : "source-over";
  ctx.strokeStyle = erase ? "#000" : stroke.color;
  ctx.globalAlpha = erase ? 1 : stroke.alpha;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (points.length === 1) {
    const [x, y, p] = points[0]!;
    ctx.lineWidth = base * pressureScale(stroke, p);
    ctx.beginPath();
    ctx.moveTo(x * w, y * h);
    ctx.lineTo(x * w + 0.02, y * h + 0.02);
    ctx.stroke();
  } else {
    for (let i = Math.max(1, from); i < points.length; i++) {
      const a = points[i - 1]!;
      const b = points[i]!;
      ctx.lineWidth = base * pressureScale(stroke, b[2]);
      ctx.beginPath();
      ctx.moveTo(a[0] * w, a[1] * h);
      ctx.lineTo(b[0] * w, b[1] * h);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Textmarker deckend überlagern würde abdunkeln – daher pro Strich als Ganzes zeichnen. */
export function drawStrokes(ctx: CanvasRenderingContext2D, strokes: readonly (Stroke | DraftStroke)[]): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  for (const stroke of strokes) drawStroke(ctx, stroke);
}

let seq = 0;
export function newStrokeId(): string {
  seq += 1;
  return `s-${Date.now().toString(36)}-${seq}`;
}

/** Normalisierte Strichstärke aus Größenstufe (1–8) und Anzeigebreite der Seite. */
export function strokeSize(tool: Tool, level: number, displayWidth: number): number {
  if (tool === "eraser") return 2 * ERASER_RADIUS * Math.max(0.3, level / 3);
  return Math.min(0.2, (Math.max(1, level) * TOOL_WIDTH[tool]) / Math.max(1, displayWidth));
}
